import validate from 'validate.js';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import { SagisConfig } from 'containers/AppContainer/typings';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { extractTimeLineObject } from 'containers/TransactionManagerTimeLine/utils';
import { RC_CORPOREAL_DAMAGE_CAUSED_TO_THIRD_PARTIES } from 'containers/CourtManagerLegalProceedings/constants';
import { getVictimAge } from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import { REGULATIONS } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import {
  getBeneficiaryRecipient,
  getClaimKey,
  getRealBeneficiary,
  getRegulationStatus,
  getRiskKey,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { INJURY_POST_DECEASED } from 'containers/PreOpeningPage/constants';
import {
  CONST_TRANSACTION,
  PAYMENT_NATURE_TRANSACTION,
  REGULATION_TRANSACTION_VALIDATION,
  TRANSACTION_STEPS,
} from 'containers/TransactionManagerTimeLine/constants';
import {
  LINK_BENEFICIARY_RECIPIENT,
  RECIPIENT,
  REGULATION_PAYMENT_STATUS_ON_HOLD,
  REGULATION_PAYMENT_STATUS_TO_PAY,
  TRANSACTION,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { isMinorVictim } from 'containers/PreOpeningPage/utils';
import { TUTOR } from 'containers/data-structure';
import messages from 'containers/TransactionManagerTimeLine/OfferRegulation/messages';
import {
  BeneficiaryType,
  VictimType,
  TransactionType,
} from 'typings/globalTypes';

export const formatDocumentRegulation = (
  regulationInfo: any,
  sagisClaim: any,
  responsabilitySharedRate: any,
  victimInfo: SagisConfig[],
  listings: any,
) => {
  const documentHeader = getDocumentHeader(
    regulationInfo,
    sagisClaim,
    responsabilitySharedRate,
    victimInfo,
    listings,
  );
  return {
    ...documentHeader,
    BNEJTaxAmount: 0,
    BNEJNumber: '',
    bankFees: 0,
    otherFees: 0,
    deductionRate: 0,
    deductionType: 'P',
    directIndemnityCosts: 0,
  };
};

export const getVictimDetails = (
  victim: VictimType,
  beneficiaries: BeneficiaryType[],
) => {
  const minor =
    getVictimAge(
      get(victim, 'id'),
      [{ ...victim }],
      moment().format('DD/MMM/YYYY'),
    ) < 18;
  if (!isEmpty(beneficiaries)) {
    return beneficiaries.map((nextOfKin: any) => {
      const isMinorBeneficiary = !isEmpty(get(nextOfKin, 'dateOfBirth', null))
        ? getVictimAge(
            nextOfKin?.id,
            get(victim, 'beneficiaries', []),
            moment().format('DD/MMM/YYYY'),
          ) < 18
        : Number(get(nextOfKin, 'age', 0)) < 18;
      return {
        victimKey: parseInt(get(victim, 'victimKey', '')),
        id: get(victim, 'id'),
        victimName: `${get(victim, 'lastName')} ${get(victim, 'firstName')}`,
        nextOfKinKey: get(nextOfKin, 'nextOfKinKey', null),
        nextOfKinName: `${get(nextOfKin, 'lastName', '')} ${get(
          nextOfKin,
          'firstName',
          '',
        )}`,
        funeralFees: 0,
        arrestAmount: 0,
        executionRate: 0,
        judgementAmount: 0,
        medicalFeesReason: null,
        medicalFees: 0,
        minor: isMinorBeneficiary,
        injuryPostKey: get(victim, 'injuryPostKey'),
      };
    });
  } else {
    return [
      {
        id: parseInt(get(victim, 'id', null) as any),
        victimKey: parseInt(get(victim, 'victimKey', '')),
        victimName: `${get(victim, 'lastName')} ${get(victim, 'firstName')}`,
        minor,
        injuryPostKey: get(victim, 'injuryPostKey'),
        funeralFees: 0,
        arrestAmount: 0,
        executionRate: 0,
        judgementAmount: 0,
        medicalFeesReason: null,
        medicalFees: 0,
      },
    ];
  }
};

export const getDocumentHeader = (
  regulationInfo: any,
  sagisClaim: any,
  responsabilitySharedRate: any,
  victimInfo: any,
  listings: any,
) => {
  const riskKey = parseInt(
    getRiskKey(sagisClaim, {
      ...regulationInfo,
      riskName: RC_CORPOREAL_DAMAGE_CAUSED_TO_THIRD_PARTIES,
    }),
  );
  const claimKey = getClaimKey(sagisClaim, riskKey);
  const { lossDate } = sagisClaim;
  const natureCode = get(listings, 'EncryptDocumentNature').find(
    (_it: any) => _it.ItemDescription === TRANSACTION,
  );
  const regulationStatus = getRegulationStatus(
    get(listings, 'EncryptDocumentCorpoStatus', []),
  );
  return {
    documentHeader: {
      claimKey,
      lossDate,
      riskKey: riskKey,
      responsabilitySharedRate: responsabilitySharedRate,
      victimsDetailModel: getVictimDetails(
        victimInfo,
        get(regulationInfo, 'beneficiaries'),
      ),
      documentId: '51',
      categoryCode: '51', //quittance
      statutCode: get(regulationStatus, 'ItemKey'),
      natureCode: get(natureCode, 'ItemKey'),
      civilPartyLawyer: '',
      arrestDate: '',
      appealCourts: '',
      judgementDate: moment().format('DD/MM/YYYY'),
      requirementLevelCode: '1',
      settlementDate: moment().format('DD/MM/YYYY'),
      reference: get(regulationInfo, [REGULATIONS.BENEFICIARY_REFERENCE], ''),
      TPI: get(regulationInfo, [REGULATIONS.TPI], ''),
      cityCode: get(regulationInfo, REGULATIONS.CITY_CODE, ''),
      missionNumber: '',
    },
  };
};

export const regulationToAdd = (
  regulationInfo: any,
  sagisClaim: any,
  responsabilitySharedRate: any,
  victimInfo: any,
  listings: any,
  totalAmount: any,
  transactionInfo: TransactionType,
  beneficiariesRegulation?: any,
) => {
  const isMinor =
    getVictimAge(
      get(victimInfo, 'id'),
      [{ ...victimInfo }],
      moment().format('DD/MMM/YYYY'),
    ) < 18;
  return {
    creator: currentUserStorage.get().username,
    isWorkflowEnabled: featureService.hasFeature(
      FEATURES.REGULATION_WORKFLOW_VALIDATION,
    ),
    isTransaction: featureService.hasFeature(FEATURES.TRANSACTION),
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    isCompletedTransaction:
      get(victimInfo, 'injuryPostKey') === INJURY_POST_DECEASED
        ? isEmpty(beneficiariesRegulation)
        : true,
    claimNumber: get(sagisClaim, 'code'),
    claimId: get(victimInfo, 'claimId'),
    provision: {},
    transactionId: get(transactionInfo, 'id'),
    victimSettlementDemands: [
      {
        encryptDocumentToCreate: {
          ...formatDocumentRegulation(
            regulationInfo,
            sagisClaim,
            responsabilitySharedRate,
            victimInfo,
            listings,
          ),
        },
        beneficiary: {
          key: `${get(victimInfo, 'victimKey')}|VICTIM`,
          countryCode: get(regulationInfo, 'countryCode'),
          claimNumber: get(sagisClaim, 'claimNumber'),
          townCode: get(regulationInfo, 'townCode'),
          beneficiaryReference: get(regulationInfo, 'beneficiaryReference'),
          PaymentNatureCode: get(regulationInfo, 'PaymentNatureCode'),
          paymentModeCode: get(regulationInfo, 'paymentModeCode'),
          [REGULATIONS.ADDITIONAL_ADDRESS]: isMinor
            ? RECIPIENT.CNRA
            : getRealBeneficiary(
                { regulationInfo: regulationInfo },
                get(listings, 'EncryptDocumentCity', []),
              ),
          [REGULATIONS.BENEFICIARY_RECIPIENT]: isMinor
            ? RECIPIENT.CNRA
            : getBeneficiaryRecipient(
                { regulationInfo: regulationInfo },
                get(listings, 'EncryptDocumentCity', []),
              ),
          [REGULATIONS.REAL_BENEFICIARY]: isMinor
            ? RECIPIENT.CNRA
            : getRealBeneficiary(
                { regulationInfo: regulationInfo },
                get(listings, 'EncryptDocumentCity', []),
                false,
              ),
          [REGULATIONS.PAYMENT_CHANNEL]: get(regulationInfo, [
            REGULATIONS.PAYMENT_CHANNEL,
          ]),
          [REGULATIONS.LINK_TO_BENEFICIARY]: isMinor
            ? get(
                get(listings, 'LinesOfRecipt', []).find(
                  (lineOfReceipt: SagisConfig[]) =>
                    get(lineOfReceipt, 'ItemDescription', '') ===
                    LINK_BENEFICIARY_RECIPIENT.CNRA,
                ) || {},
                'ItemKey',
                '',
              )
            : get(regulationInfo, [REGULATIONS.LINK_TO_BENEFICIARY], ''),
          JudgementDate: null,
          PaymentStatusCode: featureService.hasFeature(
            FEATURES.REGULATION_WORKFLOW_VALIDATION,
          )
            ? get(
                get(listings, 'PaymentStatus', []).find(
                  (paymentStatus: SagisConfig) =>
                    paymentStatus.ItemDescription ===
                    REGULATION_PAYMENT_STATUS_ON_HOLD,
                ) || {},
                'ItemKey',
                '',
              )
            : //to-edit
              get(
                get(listings, 'PaymentStatus', []).find(
                  (paymentStatus: SagisConfig) =>
                    paymentStatus.ItemDescription ===
                    REGULATION_PAYMENT_STATUS_TO_PAY,
                ) || {},
                'ItemKey',
                '',
              ),
          Amount: totalAmount,
        },
        judgementId: null,
        opposingLawyer: null,
      },
    ],
  };
};

export const getTransactionPaymentNature = (listings: any) => {
  return get(listings, 'PaymentNature', []).filter(
    (el: any) => el.ItemKey === PAYMENT_NATURE_TRANSACTION,
  );
};

export const getTransactionPaymentChannel = (listings: any) => {
  return get(listings, 'PaymentChannel', []).filter(
    (item: any) => item.ItemGroup === PAYMENT_NATURE_TRANSACTION,
  );
};

export const formatTransactionRegulations = (regulations: any) => {
  const transactionRegulations = regulations?.filter(
    (item: any) =>
      item.settlementPhase === CONST_TRANSACTION && !item.isDeleted,
  );
  if (transactionRegulations) {
    return transactionRegulations.map((el: any) =>
      String(el.sagisRegulationId),
    );
  } else {
    return [];
  }
};

export const getRegulations = (
  sagisRegulation: any,
  regulationIds: any,
  victimKey: any,
) => {
  return sagisRegulation.find(
    (el: any) =>
      regulationIds.includes(el.paymentId) &&
      String(
        get(
          el,
          'documentChiffre.documentChiffreCorporel.documentHeader.victimsDetailModel.[0].victimKey',
        ),
      ) === String(victimKey),
  );
};

export const formatRecapRegulation = (
  victimKey: any,
  regulationIds: any,
  sagisRegulation: any,
  listings: any,
  beneficiaries: any,
) => {
  if (!isEmpty(sagisRegulation) && sagisRegulation) {
    const sagisRegulationInfo = getRegulations(
      sagisRegulation,
      regulationIds,
      victimKey,
    );
    if (!isEmpty(sagisRegulationInfo)) {
      return {
        ...sagisRegulationInfo,
        [REGULATIONS.LINK_TO_BENEFICIARY]: get(
          get(listings, 'LinesOfRecipt').find((_el: any) =>
            _el.ItemDescription === get(sagisRegulationInfo, 'isAgentRecipient')
              ? LINK_BENEFICIARY_RECIPIENT.INSURED
              : LINK_BENEFICIARY_RECIPIENT.AGENT,
          ),
          'ItemKey',
        ),
        [REGULATIONS.COUNTRY_CODE]: get(
          get(listings, 'villes').find(
            (_it: any) =>
              String(_it.ItemKey) ===
              String(get(sagisRegulationInfo, 'townCode')),
          ),
          'ItemGroup',
        ),
        [REGULATIONS.TOWN_CODE]: String(get(sagisRegulationInfo, 'townCode')),
        [REGULATIONS.BENEFICIARY_RECIPIENT]:
          get(sagisRegulationInfo, 'recipient', '') === RECIPIENT.CNRA
            ? RECIPIENT.OTHERS
            : get(sagisRegulationInfo, 'recipient', '').split(' ')[0],
        [REGULATIONS.CITY_CODE]: String(
          get(
            sagisRegulationInfo,
            'documentChiffre.documentChiffreCorporel.documentHeader.cityCode',
          ),
        ),
        beneficiaries: !isEmpty(beneficiaries)
          ? get(
              sagisRegulationInfo,
              'documentChiffre.documentChiffreCorporel.documentHeader.victimsDetailModel',
              [],
            ).map((el: any) =>
              beneficiaries.find(
                (item: any) => item.nextOfKinKey === el.nextOfKinKey,
              ),
            )
          : [],
      };
    } else {
      return {};
    }
  } else {
    return {};
  }
};

export const renderRegulationTransactionStep = (
  regulationValidationHistory: any,
) => {
  return REGULATION_TRANSACTION_VALIDATION[
    regulationValidationHistory.length - 1
  ];
};

export const getTransactionEvaluation = (
  transactionOffer: any,
  victimKey: any,
  dahirAmounts: any = [],
) => {
  if (!isEmpty(transactionOffer)) {
    const evaluations = dahirAmounts
      .filter((item: any) => {
        return String(item.victimKey) === String(victimKey) &&
          parseFloat(transactionOffer.offerAmount) ===
            parseFloat(item.rcAmount) &&
          parseFloat(transactionOffer.totalOfferAmount) ===
            parseFloat(item.totalBlesse)
          ? {
              ...item,
              evaluationDate: item.evaluationDate,
              rcAmount: item.rcAmount,
            }
          : null;
      })
      .filter((item: any) => !isEmpty(item));
    return get(evaluations, '[0]');
  }
};
export const filterBeneficiariesWithRegulations = (
  sagisRegulation: any,
  regulationIds: any,
  victimKey: any,
  beneficiaries: any,
) => {
  if (!isEmpty(sagisRegulation) && sagisRegulation) {
    const sagisRegulationInfo = sagisRegulation.filter(
      (el: any) =>
        regulationIds.includes(el.paymentId) &&
        get(
          el,
          'documentChiffre.documentChiffreCorporel.documentHeader.victimsDetailModel',
          [],
        )
          .map((item: any) => String(item.victimKey))
          .includes(String(victimKey)),
    );
    const regulationBeneficiary = sagisRegulationInfo
      .map((it: any) =>
        get(
          it,
          'documentChiffre.documentChiffreCorporel.documentHeader.victimsDetailModel',
          [],
        ).map((el: any) => String(el.nextOfKinKey)),
      )
      .flat();
    return beneficiaries.filter(
      (item: any) => !regulationBeneficiary.includes(String(item.nextOfKinKey)),
    );
  } else {
    return beneficiaries;
  }
};

export const formatEmptyList = (value: any) => {
  return isEmpty(value) ? [] : value;
};

export const formatCorpoRegulationIds = (corpoRegulation: any) => {
  return isEmpty(corpoRegulation)
    ? []
    : corpoRegulation.map((el: any) => el.sagisRegulationId);
};
export const formatBodyOfDocument = (
  claimNumber: string,
  transactionInfo: TransactionType,
  claimInfo: any,
  policyInfo: any,
  offerInfo: any,
  listings: any,
) => {
  return {
    claimNumber: claimNumber,
    victimName:
      get(transactionInfo, 'victim.firstName') +
      ' ' +
      get(transactionInfo, 'victim.lastName'),
    victimCin: get(transactionInfo, 'victim.cin'),
    victimBirthDate: moment(get(transactionInfo, 'victim.dateOfBirth')).format(
      'DD/MM/YYYY',
    ),
    tutorFullName:
      get(transactionInfo, 'tutorInfo.firstName') +
      ' ' +
      get(transactionInfo, 'tutorInfo.lastName'),
    tutorCin: get(transactionInfo, 'tutorInfo.cin'),
    referenceCapital: get(offerInfo, 'referenceCapital', 0),
    rateITT: get(offerInfo, 'rateITT', 0),
    rateIPP: get(offerInfo, 'rateIPP', 0),
    amount: get(offerInfo, 'rcAmount', 0),
    itt: get(offerInfo, 'itt', 0),
    ipp: get(offerInfo, 'ipp', 0),
    ratePretium: get(offerInfo, 'ratePretium', 0),
    pretium: get(offerInfo, 'pretium', 0),
    revenu: get(offerInfo, 'revenu', 0),
    aestheticDamageWithConsequenceNature: getValueByPercentage(
      get(listings, 'aestheticDamageWithConsequence', []),
      get(offerInfo, 'rateAestheticDamageWithConsequence', 0),
    ),
    aestheticDamageWithConsequenceRate: get(
      offerInfo,
      'rateAestheticDamageWithConsequence',
      0,
    ),
    aestheticDamageWithConsequenceAmount: get(
      offerInfo,
      'aestheticDamageWithConsequence',
      0,
    ),
    aestheticDamageWithoutConsequenceNature: getValueByPercentage(
      get(listings, 'aestheticDamageWithoutConsequence', []),
      get(offerInfo, 'rateAestheticDamageWithoutConsequence', 0),
    ),
    aestheticDamageWithoutConsequenceRate: get(
      offerInfo,
      'rateAestheticDamageWithoutConsequence',
      0,
    ),
    aestheticDamageWithoutConsequenceAmount: get(
      offerInfo,
      'aestheticDamageWithoutConsequence',
      0,
    ),
    schoolingPrejudiceNature: getValueByPercentage(
      get(listings, 'schoolingPrejudice', []),
      get(offerInfo, 'rateSchoolingPrejudice', 0),
    ),
    schoolingPrejudiceRate: get(offerInfo, 'rateSchoolingPrejudice', 0),
    schoolingPrejudiceAmount: get(offerInfo, 'schoolingPrejudice', 0),
    careerPrejudiceNature: getValueByPercentage(
      get(listings, 'schoolingPrejudice', []),
      get(offerInfo, 'rateCareerPrejudice', 0),
    ),
    careerPrejudiceRate: get(offerInfo, 'rateCareerPrejudice', 0),
    careerPrejudiceAmount: get(offerInfo, 'careerPrejudice', 0),
    responsabilityRate: get(offerInfo, 'responsabilityRate', 0),
    rateRecoveryNature: getValueByPercentage(
      get(listings, 'thirdPartyAppeals', []),
      get(offerInfo, 'rateRecovery', 0),
    ),
    rateRecoveryAmount: get(offerInfo, 'rateRecovery', 0),
    rateRecovery: get(offerInfo, 'rateRecovery', 0),
    isMinor: isMinorVictim(
      get(transactionInfo, 'victim'),
      moment(get(claimInfo, 'claim.lossDate')).format('DD/MM/YYYY'),
    ),
    medicalPharmaceuticalFees: get(offerInfo, 'medicalPharmaceuticalFees', 0),
    lossDate: moment(get(claimInfo, 'claim.lossDate')).format('DD/MM/YYYY'),
    registrationNumber: get(policyInfo, 'sagis.policy.registrationNumber'),
    vehcileMarque: get(policyInfo, 'sagis.policy.vehicleMark'),
    driverName:
      get(policyInfo, 'sagis.policy.driverFirstName') +
      ' ' +
      get(policyInfo, 'sagis.policy.driverName'),
    suscribtionName:
      get(policyInfo, 'sagis.policy.clientFirstName') +
      ' ' +
      get(policyInfo, 'sagis.policy.clientLastName'),
    fullNameDoctor: get(
      get(listings, 'intervenants')?.find(
        (item: any) =>
          Number(item.ItemKey) ===
          Number(
            get(
              extractTimeLineObject(
                transactionInfo,
                TRANSACTION_STEPS.EXPERTISE,
              ),
              'missionExpertiseVictim.expertCode',
            ),
          ),
      ),
      'ItemDescription',
    ),
  };
};

export const getEvaluationByKey = (
  evaluationKey: string = '',
  dahirAmounts: any = [],
) => {
  return (
    dahirAmounts.find(
      (item: any) => String(item.evaluationKey) === String(evaluationKey),
    ) || {}
  );
};

export const getValueByPercentage = (list: [], percentage: string) => {
  return get(
    list.find((el: any) => String(el.percentage) === String(percentage)),
    'title',
  );
};
export const tutorValidator = (tutorInfo: any) => {
  return validate(tutorInfo, tutorConstraints(tutorInfo));
};

export const tutorConstraints = (_tutor: any) => {
  return {
    [TUTOR.CIN]: {
      presence: true,
      format: {
        pattern: /[A-Za-z]{1,2}\d{1,6}[A-Za-z]?|\d{1,7}[A-Za-z]/,
        message: messages.cinInvalid,
      },
    },
    [TUTOR.FIRST_NAME]: {
      presence: true,
    },
    [TUTOR.LAST_NAME]: {
      presence: true,
    },
  };
};
